import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STUDY_IMG = "https://static.prod-images.emergentagent.com/jobs/6d089f85-473b-4449-a6e3-5eb88daae93b/images/11dfd37d084722ce5fcb1c2980dccbc43962a3e05db7a90d0a7919bd0bcbb860.jpeg";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/tenant", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.phone);
      toast.success("Account created successfully!");
      navigate("/tenant");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-amber-500" />
        <img src={STUDY_IMG} alt="Students studying" className="w-full h-full object-cover mix-blend-multiply opacity-50" />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <h2 className="font-heading text-4xl font-black text-slate-900 tracking-tighter mb-2">Join Legacy Heights</h2>
            <p className="text-slate-800/70 text-lg">Create your account and start your application today.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2.5 mb-8">
              <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="font-heading text-lg font-extrabold tracking-tight leading-none text-slate-900">LEGACY</span>
                <span className="font-heading text-[10px] font-bold tracking-[0.3em] block leading-none text-sky-600">HEIGHTS</span>
              </div>
            </Link>
            <h1 className="font-heading text-3xl font-black text-slate-900 tracking-tighter">Create Account</h1>
            <p className="text-slate-500 mt-1">Sign up to apply for accommodation.</p>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <Input id="name" value={form.name} onChange={update("name")} placeholder="John Doe" className="h-12 mt-1.5 border-slate-200" required data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="your@email.com" className="h-12 mt-1.5 border-slate-200" required data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={update("password")} placeholder="Min. 6 characters" className="h-12 mt-1.5 border-slate-200" required data-testid="register-password-input" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-slate-700">Phone (optional)</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="+263 7XX XXX XXX" className="h-12 mt-1.5 border-slate-200" data-testid="register-phone-input" />
            </div>
            <Button type="submit" className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-none font-bold text-base" disabled={loading} data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-sky-600 font-semibold hover:underline" data-testid="goto-login">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
